import type { Component, JSX } from 'solid-js'
import { SITE } from '~/lib/site'

/**
 * 834CH についてのご案内（要件定義 14 章）。
 *
 * 非公式であること、動画の扱い、権利者からの連絡先をまとめて示す。
 *
 * ヘッドレス UI ライブラリではなく、ネイティブの <dialog> を使っている。
 * showModal() がフォーカストラップ・Esc での閉じ・背面の不活性化・
 * フォーカス復帰までを担ってくれるので、バンドルを増やす理由がない（要件定義 13 章）。
 */
export const AboutDialog: Component = () => {
  let dialog!: HTMLDialogElement

  return (
    <>
      <button
        type="button"
        onClick={() => dialog.showModal()}
        class="shrink-0 whitespace-nowrap underline underline-offset-2 hover:text-ch-text"
      >
        834CH について
      </button>

      {/* キーボードからは Esc で閉じられる（<dialog> の標準動作）ため、
          背景クリックに対応するキーボードイベントは不要 */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Esc で閉じられるため */}
      <dialog
        ref={dialog}
        // クリックが背景（::backdrop）に届いたら閉じる
        onClick={(event) => {
          if (event.target === dialog) dialog.close()
        }}
        class="m-auto max-h-[85dvh] w-[calc(100%-2rem)] max-w-lg rounded-ch border border-ch-border bg-ch-surface p-5 text-sm leading-relaxed text-ch-text backdrop:bg-black/70"
      >
        <div class="flex items-start justify-between gap-4">
          <h2 class="text-base font-bold">834CH について</h2>
          <button
            type="button"
            onClick={() => dialog.close()}
            class="shrink-0 rounded px-2 py-1 text-ch-muted hover:text-ch-text"
          >
            閉じる
          </button>
        </div>

        <p class="mt-3 text-ch-muted">{SITE.description}</p>

        <Section title="非公式のファンサイトです">
          834CH は個人が運営する非公式のファンプロジェクトです。
          アーティスト・所属事務所・レコード会社・YouTube およびその関係各社とは一切関係がなく、
          公式サイトや公式サービスではありません。
        </Section>

        <Section title="動画の扱い">
          放送している動画はすべて YouTube 上に公開されているもので、YouTube
          公式プレイヤーを通じてそのまま再生しています。 834CH
          が動画ファイルをダウンロード・複製・再配信することはありません。
          再生・一時停止・音量などの操作、および YouTube の帰属表示は
          公式プレイヤーのものをそのまま利用しています。
        </Section>

        <Section title="番組編成について">
          動画は独自の番組表に沿って放送され、アクセスした時刻に応じて途中から再生されます。
          収集の対象は、アーティスト公式・レーベル公式・テレビ局／ラジオ局公式など、
          運営者が許可したチャンネルに限っています。
        </Section>

        <Section title="実況について">
          放送中の番組について書き込めます。コメントは番組が切り替わると消えます。
          ユーザー登録はなく、表示名は接続のたびに自動で割り当てています。
        </Section>

        <Section title="リアクションについて">
          番組に送られたリアクションは、その動画のどのあたりで押されたかだけを記録し、
          同じ動画が次に放送されたときに、同じところで流し直しています。
          過去の放送で押されたものは薄く表示されるので、いま押されたものと見分けが付きます。
          誰が押したかは記録していません。
        </Section>

        <Section title="アクセス解析について">
          利用状況を把握して改善に役立てるため、Google
          アナリティクスを利用しています。閲覧されたページや滞在時間などが Cookie
          を通じて記録されますが、個人を特定する情報は取得していません。
        </Section>

        <Section title="ご感想・ご要望">
          「こんな番組が見たい」「こういう機能があるとうれしい」といったご要望も歓迎します。
          個人が運営している小さなサイトなので、すべてにお応えできるとは限りませんが、
          いただいたご意見は今後の編成や機能の参考にさせていただきます。
          <ContactLink />
        </Section>

        <Section title="権利者・関係者の方へ">
          掲載している動画の除外をご希望の場合は、下記よりご連絡ください。
          確認のうえ、速やかに番組編成から除外いたします。
          <ContactLink />
        </Section>
      </dialog>
    </>
  )
}

/** 連絡先へのリンク。用件が違うだけで宛先は同じなので、部品にしている */
const ContactLink: Component = () => (
  <a
    href={SITE.contact.url}
    target="_blank"
    rel="noopener noreferrer"
    class="mt-2 block text-ch-accent underline underline-offset-2"
  >
    {SITE.contact.label}
  </a>
)

const Section: Component<{ title: string; children: JSX.Element }> = (props) => (
  <section class="mt-4">
    <h3 class="text-xs font-semibold tracking-[0.14em] text-ch-muted">{props.title}</h3>
    <p class="mt-1">{props.children}</p>
  </section>
)
